import { useState } from "react";
import { termsReady } from "../lib/termsOfUse";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { startRegistration } from "@simplewebauthn/browser";
import { useAuth } from "../context/AuthContext";
import api, { formatApiErrorDetail } from "../lib/api";
import { safeRedirectTarget } from "../lib/redirectTarget";
import { homePathForRole } from "../lib/homePath";
import { USAGE_DISCLOSURE_SHORT } from "../lib/usageDisclosure";
import { CompassLogo } from "../components/CompassLogo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { arrivalToastSuppressed } from "../lib/arrivalToastGate";

// #186's brand pair, matching the landing page a visitor arrives from.
const NAVY = "#0D1B3E";
const GREEN = "#5D9D35";

// The API requires 10 characters (`registerSchema` in api/src/routes/auth.ts).
// The form used to say 6, so the browser happily submitted a 6-character
// password and the API answered 400 with nothing pointing at the password
// field — the same class of client/API disagreement as #89.
//
// Exported (issue #753) so `SignUpNew.jsx` — the redrawn screen behind
// `newUi("signup")` — shares this one number instead of a second literal
// `10` that could drift from the one the API actually enforces.
export const MIN_PASSWORD = 10;

// The band below which a parent or guardian holds the sign-in (#169, Martin's
// credential-handover decision of 2026-08-07). The account row is the
// player's either side of this line; only who holds the password changes.
// Exported for the admin console's set-DOB dialog (#569), which must show
// the same guardian note at the same threshold — two copies of this number
// is how the form and the admin surface would come to disagree about who
// holds a sign-in.
export const GUARDIAN_HELD_BELOW_AGE = 11;

/**
 * Today's CIVIL date in New Zealand, as `YYYY-MM-DD`.
 *
 * Not UTC, and not the browser's local zone. This must agree exactly with
 * `todayInNewZealand` in `api/src/lib/dateOfBirth.ts`: if the form and the
 * API disagree about what day it is, the form routes a child down one path
 * and the API judges them by another. UTC is the wrong anchor because New
 * Zealand runs UTC+12/+13 — for the 12-13 hours between local midnight and
 * UTC midnight, today in Auckland is tomorrow by UTC's reckoning, which is
 * how a newborn's date of birth came to be rejected as "in the future".
 * `Intl` carries the IANA database, so DST resolves itself; never hardcode
 * an offset.
 */
export function nzToday(now = new Date()) {
  const parts = new Intl.DateTimeFormat("en-NZ", {
    timeZone: "Pacific/Auckland",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(now);
  const get = (type) => (parts.find((p) => p.type === type) || {}).value || "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

/**
 * Whole years between an ISO `YYYY-MM-DD` date of birth and today in New
 * Zealand, or null if the string isn't a usable past date. Deliberately
 * calendar-based rather than milliseconds/365.25 — an eleventh birthday is
 * the threshold the guardian model turns on, so being a day out around leap
 * years or a timezone boundary is not acceptable.
 */
export function ageFromDob(dob, now = new Date()) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dob || "")) return null;
  const [y, m, d] = dob.split("-").map(Number);
  const born = new Date(Date.UTC(y, m - 1, d));
  // Round-trip check rejects "2026-02-30" and friends, which `Date.UTC`
  // silently rolls forward into March.
  if (born.getUTCFullYear() !== y || born.getUTCMonth() !== m - 1 || born.getUTCDate() !== d) return null;

  // Both sides are now `YYYY-MM-DD`, so "is it in the future" is a plain
  // string comparison and no timezone arithmetic is involved at all — which
  // is exactly what went wrong when this compared Date objects.
  const today = nzToday(now);
  if (dob > today) return null;
  const [ty, tm, td] = today.split("-").map(Number);
  let age = ty - y;
  const hadBirthday = tm > m || (tm === m && td >= d);
  if (!hadBirthday) age -= 1;
  return age;
}

export default function Register() {
  const { user, registerResult, completePasskeySignUpResult } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  // Issue #257, the sign-up half: a shared link followed by someone without
  // an account reaches this form via /login's "Create one", which forwards
  // the captured destination. Same validator, same fallback — see
  // `lib/redirectTarget.js`.
  // Issue #714: the fallback is ROLE-AWARE, exactly as Login.jsx's is. A
  // coach's home is My Players (#384 item 3), and sign-up can create a
  // coach now, so a hardcoded player home here would land one on a page the
  // router immediately bounces them off. `user` is null until the account
  // exists; `homePathForRole` answers the dashboard for that, which is what
  // the guard below wants anyway.
  const target = safeRedirectTarget(location.state?.from, homePathForRole(user?.role));
  const [form, setForm] = useState({
    display_name: "",
    email: "",
    password: "",
    role: "player",
    date_of_birth: "",
  });
  const [guardianConfirmed, setGuardianConfirmed] = useState(false);
  const [dobError, setDobError] = useState("");
  const [busy, setBusy] = useState(false);
  // Issue #562: the passkey signup door. Feature-detected like Login.jsx's
  // sign-in button — an unsupported browser gets no affordance rather than
  // a doomed ceremony.
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const passkeySupported = typeof window !== "undefined" && !!window.PublicKeyCredential;

  const age = ageFromDob(form.date_of_birth);
  const guardianHeld = age !== null && age < GUARDIAN_HELD_BELOW_AGE;
  const isJunior = age !== null && age < 18;

  // A signed-in visitor who lands here — and the landing page links straight
  // to it — used to be handed the sign-up form, submit it, and end up with a
  // SECOND account whose session silently replaced the one they arrived with.
  // `/` has had this guard since the landing page existed; `/login` and
  // `/register` did not, and the front door is what made the asymmetry
  // reachable. After every hook above, so hook order stays unconditional.
  // `target` rather than a hardcoded /dashboard since #257 — same reasoning
  // as Login.jsx: this branch also catches the re-render that follows a
  // successful `register()` in the same tick as the `nav()` below.
  if (user) return <Navigate to={target} replace />;

  // The client-side date-of-birth sanity gate, shared by BOTH doors (#562):
  // the guardian model turns on this field, so the passkey path must not be
  // a way around the same checks the password path makes.
  const dobLooksWrong = () => {
    if (age === null) {
      setDobError("Enter the player's date of birth as a real date in the past.");
      return true;
    }
    if (age > 120) {
      setDobError("That date of birth doesn't look right — please check it.");
      return true;
    }
    setDobError("");
    return false;
  };

  /**
   * The body BOTH doors post, minus the credential each uses.
   *
   * Issue #718: `guardianConfirmed` lives outside `form` because it is not
   * a field of the account — it is a statement by the adult filling the
   * form in — and it used to live nowhere else either: ticking the box
   * enabled the button and travelled no further, so a parent confirmed
   * something in writing and nothing was written down. This function is the
   * one place that knows both halves, so both doors send the same thing.
   */
  const signupPayload = () => ({ ...form, guardian_confirmed: guardianConfirmed });

  const submit = async (e) => {
    e.preventDefault();
    if (dobLooksWrong()) return;
    setBusy(true);
    // #1183: the reason rides in the result — `useAuth().error` is the render
    // before this request, so it was always stale here.
    const result = await registerResult(signupPayload());
    const created = result.ok ? result.user : false;
    setBusy(false);
    // `replace` for the same reason Login.jsx uses it: nobody should be able
    // to press Back onto a sign-up form they have just completed.
    //
    // Issue #714: where they land is decided by the role the account was
    // actually created with — read off the server's response, the way
    // Login.jsx reads it off the sign-in response, and not off `form.role`
    // (which is what the visitor asked for, not what exists) nor off
    // context (which has not re-rendered this handler yet).
    if (created) {
      // D109 (PR #846, second review) — see arrivalToastGate.js. Deliberately
      // a second call to `safeRedirectTarget`, not a shared `dest` variable,
      // so `Register.choices.test.js`'s own pin — "navigates by the role the
      // SERVER created, via homePathForRole" — keeps matching this exact,
      // already-reviewed navigation line unchanged.
      if (!arrivalToastSuppressed(safeRedirectTarget(location.state?.from, homePathForRole(created.role))))
        toast.success("Account created!");
      nav(safeRedirectTarget(location.state?.from, homePathForRole(created.role)), { replace: true });
    } else toast.error(result.error || "Registration failed");
  };

  // Issue #562: create the account with a passkey instead of a password —
  // same fields, same validation, same guardian gate; only the credential
  // differs. The button is type="button", so the form's native `required`
  // never fires: the name/email/date checks are made explicitly here (the
  // password input's requirement deliberately does NOT apply — there is no
  // password on this path). The two-phase exchange mirrors Login.jsx's
  // sign-in door: options from the server (the fields travel with the
  // request and are validated there with password registration's own
  // schema), the browser ceremony verbatim, then the verify through
  // AuthContext (`completePasskeySignUp`) so context and redirects are the
  // shared machinery. A dismissed ceremony (NotAllowedError/AbortError) is
  // silence, not an error toast — dismissing the browser's own sheet was
  // the visitor's act.
  const signUpWithPasskey = async () => {
    if (dobLooksWrong()) return;
    if (!form.display_name.trim() || !form.email.trim()) {
      toast.error("Fill in the player's name and the email address first.");
      return;
    }
    setPasskeyBusy(true);
    try {
      // Issue #718: the guardian confirmation rides along here too. This
      // door has the same guardian gate on the button and the same server
      // rule behind it — a gate only the password door applies is not a
      // gate. Still no password and still no role: this door creates a
      // player, as it always has (#562).
      const fields = {
        email: form.email,
        display_name: form.display_name,
        date_of_birth: form.date_of_birth,
        guardian_confirmed: guardianConfirmed,
      };
      const { data: options } = await api.post("/auth/passkey/register-new/options", fields);
      const ceremony = await startRegistration({ optionsJSON: options });
      const result = await completePasskeySignUpResult(fields, ceremony);
      const created = result.ok ? result.user : false;
      if (created) {
        // D109 (PR #846, second review) — see arrivalToastGate.js.
        if (!arrivalToastSuppressed(target)) toast.success("Account created!");
        nav(target, { replace: true });
      } else {
        toast.error(result.error || "Registration failed");
      }
    } catch (e) {
      if (e?.name !== "NotAllowedError" && e?.name !== "AbortError") {
        // The options call answers with the same details the password door
        // gets — "Email already registered" included — so surface them.
        toast.error(
          formatApiErrorDetail(e.response?.data?.detail) ||
            "Could not create the account with a passkey on this device.",
        );
      }
    }
    setPasskeyBusy(false);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="flex items-center justify-center p-6 sm:p-12 order-2 lg:order-1 min-w-0">
        <div className="w-full max-w-md min-w-0">
          <Link to="/" className="inline-flex lg:hidden mb-8" data-testid="register-home">
            <CompassLogo size={40} />
          </Link>
          <h2 className="font-display font-black text-3xl" style={{ color: NAVY }}>Create your account</h2>
          <p className="text-slate-500 mt-1">
            The account belongs to the player.{" "}
            <Link to="/" className="font-semibold underline" style={{ color: NAVY }} data-testid="link-landing">
              What is CricketCompass?
            </Link>
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="register-form">
            <div>
              <Label>Player's full name</Label>
              <Input data-testid="register-name" required value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                className="mt-1.5 h-11" placeholder="[Player's full name]" />
            </div>
            <div>
              <Label>Player's date of birth</Label>
              <Input data-testid="register-dob" required type="date" value={form.date_of_birth}
                onChange={(e) => { setForm({ ...form, date_of_birth: e.target.value }); setDobError(""); }}
                className="mt-1.5 h-11" />
              {dobError
                ? <p className="text-xs text-red-600 mt-1" data-testid="register-dob-error">{dobError}</p>
                : <p className="text-xs text-slate-500 mt-1">
                    This decides whether a parent or guardian holds the sign-in — see below.
                  </p>}
            </div>

            {/* Issue #169's guardian path, entered by date of birth rather
                than by asking the visitor to self-declare which of them they
                are. Under 11 the guardian is the operator, so the email and
                password on this form are THEIRS — saying so at the point the
                fields are filled in is the whole reason this panel exists. */}
            {guardianHeld && (
              <div className="rounded-lg border-2 p-4 min-w-0" style={{ borderColor: NAVY }} data-testid="register-guardian">
                <p className="text-[11px] font-bold uppercase tracking-[0.2em]" style={{ color: GREEN }}>
                  Under {GUARDIAN_HELD_BELOW_AGE} — a guardian sets this up
                </p>
                {/* Same rule as the landing page's junior panel: state what
                    is true today, not the handover at 11 that #169 decides
                    and nothing implements. This is the form an adult actually
                    completes, so a promise made here is the one they would
                    hold us to. */}
                <p className="mt-2 text-sm text-slate-700">
                  The account is in the player's name and stays theirs. While they are under{" "}
                  {GUARDIAN_HELD_BELOW_AGE}, a parent or guardian holds the sign-in details: use{" "}
                  <strong>your own email address</strong> below, and set a password you keep.
                </p>
                <label htmlFor="register-guardian-confirm" className="mt-4 flex items-start gap-3 text-sm text-slate-700 cursor-pointer">
                  <Checkbox
                    id="register-guardian-confirm"
                    checked={guardianConfirmed}
                    onCheckedChange={(v) => setGuardianConfirmed(v === true)}
                    data-testid="register-guardian-confirm"
                    className="mt-0.5 shrink-0"
                  />
                  <span className="min-w-0">
                    I am this player's parent or guardian and I am setting the account up for them.
                  </span>
                </label>
              </div>
            )}
            {isJunior && !guardianHeld && (
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 min-w-0" data-testid="register-junior-note">
                <p className="text-sm text-slate-700">
                  From {GUARDIAN_HELD_BELOW_AGE} the player holds their own sign-in, so the email and
                  password below are theirs. A parent or guardian should know the account exists — while
                  the player is a minor, a guardian approves the account and the links it makes.
                </p>
              </div>
            )}

            <div>
              <Label>{guardianHeld ? "Parent or guardian's email address" : "Email"}</Label>
              <Input data-testid="register-email" required type="email" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 h-11" />
            </div>
            <div>
              <Label>Password</Label>
              <Input data-testid="register-password" required type="password" minLength={MIN_PASSWORD} value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5 h-11" />
              <p className="text-xs text-slate-500 mt-1">At least {MIN_PASSWORD} characters.</p>
            </div>
            <div>
              <Label>I am a</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="register-role" className="mt-1.5 h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="player">Player</SelectItem>
                  <SelectItem value="coach">Coach</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Issue #224, Andreas's ruling of 2026-08-21: disclosure, not
                consent. The sign-up form is where the person deciding is
                actually present — for an under-11 that is the guardian
                filling this in, per #169 — and it is the last thing read
                before the account exists, which is the only moment before
                any counting could happen. Plain text, not a checkbox: a
                checkbox would claim we asked, and we did not. The full
                statement is in Settings; both come from
                `lib/usageDisclosure.js`. */}
            <p className="text-xs text-slate-500 min-w-0" data-testid="register-usage-disclosure">
              {USAGE_DISCLOSURE_SHORT}
            </p>

            {/* Issue #449 (interim, partial): the same door that carries the
                usage disclosure above also points at the fuller privacy
                notice — what the account will hold, who can see it, and how
                to ask about it. Public route, reachable before this form is
                ever submitted. */}
            <p className="text-xs text-slate-500 min-w-0" data-testid="register-privacy-link">
              Read our{" "}
              <Link to="/privacy" className="underline hover:text-slate-700">
                privacy notice
              </Link>
              {termsReady() && (
                <>
                  {" "}and{" "}
                  <Link to="/terms" className="underline hover:text-slate-700" data-testid="register-terms-link">
                    terms of use
                  </Link>
                </>
              )}{" "}
              for what we hold and who can see it.
            </p>

            <Button
              data-testid="register-submit"
              type="submit"
              disabled={busy || passkeyBusy || (guardianHeld && !guardianConfirmed)}
              className="w-full h-11 text-base font-semibold"
              style={{ background: NAVY }}
            >
              {busy ? "Creating…" : "Create account"}
            </Button>
          </form>

          {/* Issue #562: the passwordless door, beside the password form —
              Login.jsx's link-row idiom (#510's one-clean-surface rule), not
              a second stacked form. Same fields, same guardian gate (the
              disabled expression below is the submit button's), no password.
              Rendered only where WebAuthn exists at all. */}
          {passkeySupported && (
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={signUpWithPasskey}
                disabled={busy || passkeyBusy || (guardianHeld && !guardianConfirmed)}
                className="text-xs font-semibold text-slate-500 hover:text-slate-900 disabled:opacity-50"
                data-testid="register-passkey"
              >
                {passkeyBusy
                  ? "Follow your browser's prompts…"
                  : "Or create your account with a passkey — no password to remember"}
              </button>
            </div>
          )}

          <div className="mt-6 text-sm text-center text-slate-600">
            Already have an account?{" "}
            {/* Carries the captured destination back the other way (#257) —
                a visitor who bounces between these two forms deciding which
                one they need must not lose the link that sent them here. */}
            <Link to="/login" state={location.state} className="font-semibold" style={{ color: NAVY }} data-testid="link-login">Sign in</Link>
          </div>
        </div>
      </div>

      <div className="relative hidden lg:block overflow-hidden order-1 lg:order-2">
        <img src="https://images.unsplash.com/photo-1750716413756-b66624b64ce4" alt="Cricket ground" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${GREEN}D9 0%, ${NAVY}E6 100%)` }} />
        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          <Link to="/" className="bg-white rounded-lg px-3 py-2 inline-flex self-start">
            <CompassLogo size={52} />
          </Link>
          <div>
            {/* No "join thousands of players", and no AI coaching: the AI
                surfaces are flagged off (lib/featureFlags.js, issue #56) and
                the friendlies have not started. The pitch is what the app
                actually does. */}
            <h1 className="font-display font-black text-4xl sm:text-5xl leading-tight">One record, from the first innings on.</h1>
            <p className="mt-4 text-white/85 max-w-md">
              Log the match, watch the season add up, and invite the coach you choose — for exactly as long
              as you choose.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
